<template>
    <jet-dialog-modal :show="show" @close="handleCancel">
        <template #title>
            <h3 class="text-2xl">{{ mode === 'one-off' ? 'Print To' : 'Select Printer' }}</h3>
        </template>

        <template #content>
            <div v-if="loading" class="py-4 text-gray-500">Loading printers...</div>

            <div v-else-if="!printers.length" class="py-2">
                <p>No printers set up for {{ typeLabel }}. Contact a supervisor.</p>
            </div>

            <div v-else class="space-y-3 my-2">
                <jet-input-error v-if="inlineError" :message="inlineError" class="mb-2" />

                <label
                    v-for="printer in printers"
                    :key="printer.id"
                    class="flex items-start gap-x-3 p-2 rounded border"
                    :class="{
                        'opacity-50 cursor-not-allowed bg-gray-50': isDisabled(printer),
                        'cursor-pointer hover:bg-gray-50': !isDisabled(printer),
                        'border-green': selectedId === printer.id,
                    }"
                >
                    <input
                        type="radio"
                        class="mt-1"
                        :value="printer.id"
                        v-model="selectedId"
                        :disabled="isDisabled(printer)"
                    />
                    <div class="flex-1">
                        <div class="font-medium">{{ printer.name }}</div>
                        <div
                            v-if="(printer.divisions || []).length > 1"
                            class="text-sm text-gray-500"
                        >
                            {{ printer.divisions_label }}
                        </div>
                        <div v-if="printer.exclusive && printer.claimant_names.length" class="text-sm text-gray-500">
                            Claimed by {{ printer.claimant_names.join(', ') }}
                        </div>
                        <div v-else-if="!printer.exclusive && printer.claimant_names.length" class="text-sm text-gray-500">
                            In use by {{ printer.claimant_names.join(', ') }}
                        </div>
                    </div>
                </label>

                <div v-if="mode === 'one-off' && hasCheckerSession" class="pt-2">
                    <label class="flex items-center gap-x-2">
                        <jet-checkbox v-model:checked="remember" />
                        <span>Remember for the rest of my session</span>
                    </label>
                </div>
            </div>
        </template>

        <template #footer>
            <template v-if="printers.length">
                <jet-button
                    class="mr-2"
                    :disabled="!selectedId || submitting"
                    @click="handleConfirm"
                >
                    {{ mode === 'one-off' ? 'Print' : 'Select' }}
                </jet-button>
            </template>

            <jet-tertiary-button @click="handleCancel">
                Cancel
            </jet-tertiary-button>
        </template>
    </jet-dialog-modal>
</template>

<script>
import JetDialogModal from '@/Jetstream/DialogModal';
import JetButton from '@/Jetstream/Button';
import JetTertiaryButton from '@/Jetstream/TertiaryButton';
import JetInputError from '@/Jetstream/InputError';
import JetCheckbox from '@/Jetstream/Checkbox';
import { EventBus } from '@/eventBus.js';
import { usePage } from '@inertiajs/inertia-vue3';
import axios from 'axios';

export default {
    components: {
        JetDialogModal,
        JetButton,
        JetTertiaryButton,
        JetInputError,
        JetCheckbox,
    },

    data() {
        return {
            show: false,
            mode: 'claim',
            type: null,
            printers: [],
            selectedId: null,
            remember: true,
            loading: false,
            submitting: false,
            inlineError: null,
            onConfirm: null,
            onCancel: null,
            onSessionExpired: null,
            hasCheckerSessionOverride: null,
        };
    },

    computed: {
        hasCheckerSession() {
            if (this.hasCheckerSessionOverride !== null) {
                return !!this.hasCheckerSessionOverride;
            }
            return !!usePage().props.value.hasCheckerSession;
        },

        typeLabel() {
            return { wholesale: 'Wholesale', schools: 'Schools', retail: 'Retail' }[this.type] || this.type;
        },
    },

    mounted() {
        EventBus.on('open-select-printer', this.open);
    },

    beforeUnmount() {
        EventBus.off('open-select-printer', this.open);
    },

    methods: {
        isDisabled(printer) {
            return !!(printer.exclusive && printer.claimed);
        },

        isCheckerSessionExpired(error) {
            return error?.response?.status === 401
                && error?.response?.data?.message === 'invalid_checker_session';
        },

        async open(payload) {
            this.mode = payload.mode || 'claim';
            this.type = payload.type;
            this.onConfirm = payload.onConfirm || null;
            this.onCancel = payload.onCancel || null;
            this.onSessionExpired = payload.onSessionExpired || null;
            this.selectedId = null;
            this.remember = true;
            this.inlineError = null;
            this.hasCheckerSessionOverride = payload.hasCheckerSession ?? null;
            this.show = true;

            // Resolve live checker session when not explicitly provided (axios logins won't refresh Inertia props).
            if (this.hasCheckerSessionOverride === null) {
                try {
                    const { data } = await axios.get(route('orders.get_checker'));
                    this.hasCheckerSessionOverride = !!(data && data !== 'invalid_checker_session' && typeof data === 'object');
                } catch (e) {
                    this.hasCheckerSessionOverride = !!usePage().props.value.hasCheckerSession;
                }
            }

            if (payload.printers && Array.isArray(payload.printers)) {
                this.printers = payload.printers;
                const selectable = this.printers.find(p => !this.isDisabled(p));
                this.selectedId = selectable ? selectable.id : null;
                this.loading = false;
            } else {
                await this.fetchPrinters();
            }
        },

        async fetchPrinters() {
            this.loading = true;
            try {
                const { data } = await axios.get(route('printers.available'), {
                    params: { type: this.type },
                });
                this.printers = data.printers || [];
                const selectable = this.printers.find(p => !this.isDisabled(p));
                this.selectedId = selectable ? selectable.id : null;
            } catch (e) {
                this.printers = [];
                this.inlineError = 'Could not load printers.';
            } finally {
                this.loading = false;
            }
        },

        async handleConfirm() {
            if (!this.selectedId || this.submitting) return;
            this.submitting = true;
            this.inlineError = null;

            try {
                if (this.mode === 'claim' || (this.mode === 'one-off' && this.remember && this.hasCheckerSession)) {
                    try {
                        await axios.post(route('printers.assign', this.selectedId), null, {
                            params: { type: this.type },
                        });
                    } catch (e) {
                        if (e.response?.status === 409) {
                            this.printers = e.response.data.printers || this.printers;
                            this.inlineError = e.response.data.message || 'That printer was just claimed.';
                            this.selectedId = null;
                            return;
                        }
                        if (this.isCheckerSessionExpired(e)) {
                            this.handleSessionExpired();
                            return;
                        }
                        throw e;
                    }
                }

                if (typeof this.onConfirm === 'function') {
                    await this.onConfirm(this.selectedId, {
                        remember: this.remember && this.hasCheckerSession,
                    });
                }

                this.close();
            } catch (e) {
                if (this.isCheckerSessionExpired(e)) {
                    this.handleSessionExpired();
                    return;
                }
                this.inlineError = e.response?.data?.message || e.message || 'Something went wrong.';
            } finally {
                this.submitting = false;
            }
        },

        handleCancel() {
            if (typeof this.onCancel === 'function') {
                this.onCancel();
            }
            this.close();
        },

        /**
         * Close and notify the caller that the checker sublogin expired during assign/confirm.
         * Distinct from cancel so callers can clear the checker and reopen the code prompt.
         */
        handleSessionExpired() {
            const onSessionExpired = this.onSessionExpired;
            this.close();
            if (typeof onSessionExpired === 'function') {
                onSessionExpired();
            }
        },

        close() {
            this.show = false;
            this.onConfirm = null;
            this.onCancel = null;
            this.onSessionExpired = null;
            this.printers = [];
            this.selectedId = null;
            this.inlineError = null;
            this.hasCheckerSessionOverride = null;
        },
    },
};
</script>
